import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import type { FilterShipmentsDescription } from "@/Shared/ShipmentFilter/Dialogs/Internal/FilterShipmentsDialogBody";
import { FilterShipmentsDialogBody } from "@/Shared/ShipmentFilter/Dialogs/Internal/FilterShipmentsDialogBody";
import { shipmentFilterEmptyDescription } from "@/Shared/ShipmentFilter/Helpers/shipmentFilterEmptyDescription";

// Hosts the filter form in a ui-core Dialog. Matches the legacy
// contract exactly, including the quirk that cancel/close resolves an EMPTY
// filter description — i.e. dismissing the dialog clears the filters.
export function useShipmentFilterDialog(): {
  show: (
    filterDescription: FilterShipmentsDescription,
  ) => Promise<FilterShipmentsDescription>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [filterDescription, setFilterDescription] = useState<
    FilterShipmentsDescription | undefined
  >(undefined);
  const resolverRef = useRef<
    ((result: FilterShipmentsDescription) => void) | undefined
  >(undefined);

  function show(
    currentFilterDescription: FilterShipmentsDescription,
  ): Promise<FilterShipmentsDescription> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setFilterDescription(currentFilterDescription);
      setOpenCount((count) => count + 1);
      setOpen(true);
    });
  }

  function settle(result: FilterShipmentsDescription) {
    resolverRef.current?.(result);
    resolverRef.current = undefined;
  }

  const dialogs = filterDescription !== undefined && (
    <Dialog
      title="Filter Shipments"
      description="Select filters to narrow down the range of shipments you are viewing"
      key={`shipment-filter-${openCount}`}
      open={open}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          settle(shipmentFilterEmptyDescription());
        }
      }}
      // `medium`, not `extra-wide`: the four filter groups are tabs now, so
      // only one column of fields is on screen at a time. At extra-wide the
      // tab content was a narrow strip in a mostly-empty dialog.
      width="medium"
    >
      <FilterShipmentsDialogBody
        filterDescription={filterDescription}
        onConfirm={function (result) {
          settle(result);
          setOpen(false);
        }}
        onCancel={function () {
          settle(shipmentFilterEmptyDescription());
          setOpen(false);
        }}
      />
    </Dialog>
  );

  return { show, dialogs };
}
